import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Plus, XCircle, X, Clock } from 'lucide-react'
import dayjs from 'dayjs'
import { AdasAlert, AdasBox, AdasDatePicker } from '@components/wrapper-components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { DATE_FORMATS } from 'constants'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore, useOrganisationStore } from '@caradasstore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { createDateOverrides } from '../../../api/api'
import { Logger } from '../../../logger'
import { formatDate } from '../../../utils/utils'
import { cn } from 'lib/utils'

const buildTimeOptions = (step = 15) => {
  const opts = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const ampm = h < 12 ? 'AM' : 'PM'
      const display = `${String(h % 12 || 12).padStart(2, '0')}:${mm} ${ampm}`
      opts.push({ value: `${hh}:${mm}`, display })
    }
  }
  return opts
}

const SlotTimePicker = ({ value, onChange, error }) => {
  const opts = useMemo(() => buildTimeOptions(15), [])
  const current = opts.find((o) => o.value === value)
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger
        className={cn(
          'h-10 w-[140px] justify-between rounded-lg border-[#D1D5D8] bg-white px-3 text-[13px] font-medium text-[#0A1628]',
          '[&>span]:line-clamp-none [&>span]:whitespace-nowrap',
          '[&>svg.lucide-chevron-down]:hidden',
          error && 'border-error'
        )}
      >
        <SelectValue placeholder='—'>{current?.display}</SelectValue>
        <Clock className='ml-2 h-4 w-4 shrink-0 text-[#475569]' />
      </SelectTrigger>
      <SelectContent className='max-h-[260px]'>
        {opts.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.display}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

SlotTimePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool
}

const AvailabilityForm = ({ setIsAddDateOpen, dates, onSuccess, editingOverride }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [unAvailabilities, setUnAvailabilities] = useState([{ start: '12:00', end: '13:00' }])
  const [isDayUnavailable, setIsDayUnavailable] = useState(false)
  const [errors, setErrors] = useState([])
  const [showAlert, setShowAlert] = useState(false)
  const ccId = useCenterStore((store) => store.ccId)
  const currentUser = useUserStore((store) => store.currentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const organisationId = useOrganisationStore((store) => store.organisationId)

  useEffect(() => {
    if (!editingOverride) return
    setSelectedDate(dayjs(editingOverride.overrides_date))
    if (!editingOverride.slots || editingOverride.slots.length === 0) {
      setIsDayUnavailable(true)
    } else {
      setIsDayUnavailable(false)
      setUnAvailabilities(editingOverride.slots)
    }
  }, [editingOverride])

  const handleAvailabilityChange = (index, key, value) => {
    const next = unAvailabilities.map((a, i) => (i === index ? { ...a, [key]: value } : a))
    setUnAvailabilities(next)
    validateUnAvailabilities(next, index)
  }

  const handleRemoveAvailability = (idx) => {
    const next = unAvailabilities.filter((_, i) => i !== idx)
    setUnAvailabilities(next)
    validateUnAvailabilities(next)
  }

  const handleAddUnAvailability = () => {
    if (!unAvailabilities || unAvailabilities.length === 0) {
      setUnAvailabilities([{ start: '12:00', end: '13:00' }])
      return
    }
    const lastSlotEnd = unAvailabilities[unAvailabilities.length - 1].end
    const [lastEndHour, lastEndMinute] = lastSlotEnd.split(':').map(Number)
    let newStartHour = lastEndHour + 1
    let formattedEnd
    if (newStartHour >= 24) {
      formattedEnd = '23:59'
      newStartHour = 23
    } else {
      formattedEnd = `${(newStartHour + 1).toString().padStart(2, '0')}:${lastEndMinute.toString().padStart(2, '0')}`
    }
    const formattedStart = `${newStartHour.toString().padStart(2, '0')}:${lastEndMinute.toString().padStart(2, '0')}`
    setUnAvailabilities([...unAvailabilities, { start: formattedStart, end: formattedEnd }])
  }

  const handleDateSelect = (d) => {
    if (!d) return
    const newDate = dayjs(d)
    setSelectedDate(newDate)
    if (dates) {
      const foundDate = dates.find(
        (date) => formatDate(date.overrides_date, DATE_FORMATS.ISO_DATE_STRING) === formatDate(newDate, DATE_FORMATS.ISO_DATE_STRING)
      )
      if (foundDate && foundDate.slots) setUnAvailabilities(foundDate.slots)
    }
  }

  const validateUnAvailabilities = (_un, changedIndex = null) => {
    const errs = _un.map(() => false)
    const overlaps = (a, b) => a.start < b.end && a.end > b.start
    if (changedIndex === null) {
      _un.forEach((slot, i) => {
        if (i > 0 && overlaps(slot, _un[i - 1])) errs[i] = true
        if (i < _un.length - 1 && overlaps(slot, _un[i + 1])) errs[i] = true
      })
    } else {
      const c = _un[changedIndex]
      if (changedIndex > 0 && overlaps(c, _un[changedIndex - 1])) errs[changedIndex] = true
      if (changedIndex < _un.length - 1 && overlaps(c, _un[changedIndex + 1])) errs[changedIndex] = true
    }
    _un.forEach((slot, i) => {
      if (slot.start >= slot.end) errs[i] = true
    })
    if (!errs.some((e) => e === true)) setShowAlert(false)
    setErrors(errs)
  }

  useEffect(() => {
    validateUnAvailabilities(unAvailabilities)
  }, [unAvailabilities])

  const getPostData = () => {
    if (isDayUnavailable) {
      return {
        ccId,
        organisation_id: organisationId,
        availabilities: [
          {
            start_time: '00:00',
            end_time: '00:00',
            organisation_id: organisationId,
            overrides_date: selectedDate.format('YYYY-MM-DD'),
            is_unavailable: true,
            is_active: true,
            updated_by: currentUser.id
          }
        ]
      }
    }
    return {
      ccId,
      organisation_id: organisationId,
      availabilities: unAvailabilities.map((a) => ({
        start_time: a.start || null,
        end_time: a.end || null,
        organisation_id: organisationId,
        overrides_date: selectedDate.format('YYYY-MM-DD'),
        is_unavailable: false,
        is_active: true,
        updated_by: currentUser.id
      }))
    }
  }

  const handleSubmit = async () => {
    validateUnAvailabilities(unAvailabilities)
    const hasErrors = errors.some((e) => e === true)
    if (!isDayUnavailable && hasErrors) {
      setShowAlert(true)
      return
    }
    setLoading(true)
    try {
      const response = await createDateOverrides(getPostData())
      if (response?.data) {
        onSuccess()
        setIsAddDateOpen(false)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { availabilities: unAvailabilities, path: 'handleSubmit' } })
    } finally {
      setLoading(false)
    }
  }

  const isEdit = !!editingOverride

  return (
    <div className='flex flex-col'>
      <div className='flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-[#EBEDF0]'>
        <div className='min-w-0'>
          <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>
            {isEdit ? 'Edit Override' : 'Add Override'}
          </div>
          <div className='mt-0.5 text-[20px] font-bold leading-tight text-[#0A1628]'>Block a specific date</div>
        </div>
        <button
          type='button'
          onClick={() => setIsAddDateOpen(false)}
          aria-label='Close'
          className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-md text-[#475569] hover:bg-[#F8FAFC]'
        >
          <X className='h-4 w-4' />
        </button>
      </div>

      <div className='px-5 py-4 space-y-5'>
        <div>
          <div className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Date</div>
          <AdasDatePicker
            value={selectedDate.format(DATE_FORMATS.ISO_DATE_STRING)}
            format={DATE_FORMATS.ISO_DATE_STRING}
            disablePast
            onChange={handleDateSelect}
          />
        </div>

        <label className='flex items-start gap-3 rounded-lg border border-[#EBEDF0] bg-white px-3 py-3 cursor-pointer'>
          <input
            type='checkbox'
            checked={isDayUnavailable}
            onChange={() => setIsDayUnavailable((v) => !v)}
            className='mt-0.5 h-4 w-4 rounded border-[#D1D5D8]'
          />
          <span>
            <div className='text-[13px] font-bold text-[#0A1628]'>All Day Unavailable</div>
            <div className='text-[12px] text-[#6B7280]'>Blocks the entire day for bookings</div>
          </span>
        </label>

        {!isDayUnavailable && (
          <div>
            <div className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Block Time Period</div>
            <div className='space-y-2'>
              {unAvailabilities.map((a, index) => (
                <div key={index} className='flex flex-wrap items-center gap-2'>
                  <SlotTimePicker value={a.start} onChange={(v) => handleAvailabilityChange(index, 'start', v)} error={!!errors[index]} />
                  <span className='text-[13px] text-[#6B7280]'>to</span>
                  <SlotTimePicker value={a.end} onChange={(v) => handleAvailabilityChange(index, 'end', v)} error={!!errors[index]} />
                  <button
                    type='button'
                    onClick={() => handleRemoveAvailability(index)}
                    aria-label='Remove time range'
                    className='inline-flex h-8 w-8 items-center justify-center rounded-md text-[#475569] hover:text-[#0A1628]'
                  >
                    <XCircle className='h-5 w-5' />
                  </button>
                </div>
              ))}
            </div>
            <button
              type='button'
              onClick={handleAddUnAvailability}
              className='mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:text-[#1A5FD0]'
            >
              <Plus className='h-3.5 w-3.5' />
              Set Blocked Time Range
            </button>
            {showAlert && (
              <AdasAlert severity='error' className='mt-3'>
                {errors?.length > 1
                  ? 'The selected time slot overlaps with an existing blocked period. Please adjust and try again.'
                  : 'The time range for blocking is invalid.'}
              </AdasAlert>
            )}
          </div>
        )}
      </div>

      <AdasBox className='flex items-center justify-end gap-3 px-5 pb-5 pt-3 border-t border-[#EBEDF0]'>
        <button
          type='button'
          onClick={() => setIsAddDateOpen(false)}
          className='inline-flex items-center justify-center px-5 py-2.5 rounded-lg border border-[#D1D5D8] bg-white text-sm font-medium text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={handleSubmit}
          disabled={!isDayUnavailable && unAvailabilities.length === 0}
          className='inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-sidebar text-white text-sm font-medium hover:bg-sidebar/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isEdit ? 'Update Override' : 'Add Override'}
        </button>
      </AdasBox>
    </div>
  )
}

AvailabilityForm.propTypes = {
  setIsAddDateOpen: PropTypes.func.isRequired,
  dates: PropTypes.array,
  onSuccess: PropTypes.func,
  editingOverride: PropTypes.object
}

export default AvailabilityForm
